import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { lineString } from '@turf/helpers';
import { bbox } from '@turf/turf';
import type { Position } from 'geojson';
import {
    LngLat,
    LngLatLike,
    MapGeoJSONFeature,
    MapGeoJSONFeature as MapLibreMapGeoJSONFeature,
    PointLike,
} from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';

import { MapHighlightEntityService } from '@connect-services/client/app/services/map-highlight-entity.service';
import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    MapBaseSource,
    MapboxStyleWithZoom,
    MapSourceTypeEnum,
} from '@connect-services/client/components/map/map.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { SynopticMapData } from '@connect-services/client/services/broadcast-channel.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { EntityLocation } from '@connect-services/client/services/map-draw.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { DepositWithStandard } from '@connect-services/client/services/synoptic/export-synoptic-cartouche.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SynopticGraphService } from '@connect-services/client/services/synoptic/synoptic-graph.service';
import {
    GenericSynopticItem,
    GraceThdSynopticItem,
    Layer,
    LayerControllerGet200Response,
    LayerControllerGetEntities200ResponseAllOfItemsInner,
    LayersService,
    LayerStyle,
    LayerStylesService,
    MapboxStyle,
    SynopticZone,
} from '@connect-services/src/sdk/api';
import { defaultPaginationParameters, paginationMaxTake } from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export class EntityInformation {
    public constructor(
        public featureLocation?: EntityLocation,
        public featureSource?: MapBaseSource<unknown>,
        public type?: string,
        public layerStyle?: MapboxStyle | MapboxStyleWithZoom,
    ) {}
}

export const DEFAULT_LAYER_STYLE_ID = '00000000-0000-0000-0000-000000000001';

@Injectable({
    providedIn: 'root',
})
export class MapSynopticRedirectionService extends MapBaseService {
    public readonly projectId = computed(() => this.projectBloc.value()?.id || '');

    private readonly broadcastHighlightSynopticNodeChannel = new BroadcastChannel(
        BroadcastChannelNameEnum.HIGHLIGHT_SYNOPTIC_NODE,
    );
    private readonly destroyRef = inject(DestroyRef);
    private readonly homeService = inject(HomeService);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly layerStylesService = inject(LayerStylesService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly mapLayerService = inject(MapLayerService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly sdkLayersService = inject(LayersService);
    private readonly selectSynopticService = inject(SelectSynopticService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);
    private readonly synopticGraphService = inject(SynopticGraphService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        super();

        this.listenBroadcastHighlightSynopticNodeChannel();

        this.destroyRef.onDestroy(() => {
            this.homeService.synopticExtPageOpen.set(false);
            this.broadcastHighlightSynopticNodeChannel.close();
        });
    }

    public async findEntityInLayerService(
        layerFromSyno: GraceThdSynopticItem | GenericSynopticItem,
        nodeUuid: string,
        depositId: string,
        entityTypeEnum: LayerStyleTypeEnum,
    ): Promise<EntityInformation> {
        const sdkLayers: LayerControllerGet200Response = await firstValueFrom(
            this.sdkLayersService.layerControllerGet({
                deposit: depositId,
                project: this.projectId(),
                ...defaultPaginationParameters,
                take: paginationMaxTake,
            }),
        );

        const layerFound = sdkLayers.items.find((layer: Layer) =>
            this.isMatchingLayer(layer, layerFromSyno, nodeUuid, entityTypeEnum),
        );

        if (!layerFound) {
            this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

            throw new Error(this.translocoService.translate('entityNotFoundOnMap'));
        }

        AssertService.isString(layerFound.id);

        const layerEntity: LayerControllerGetEntities200ResponseAllOfItemsInner = await firstValueFrom(
            this.sdkLayersService.layerControllerGetLayersEntity({
                entityId: nodeUuid,
                id: layerFound.id,
            }),
        );

        const layerStyle: LayerStyle = await firstValueFrom(
            this.layerStylesService.layerStyleControllerGetOne({
                id: layerFound.layerStyle ?? DEFAULT_LAYER_STYLE_ID,
            }),
        );

        const featureLocation = layerEntity.geom as EntityLocation;
        const type = featureLocation?.type?.toLowerCase();
        const source = this.sourcesService.getSource(layerFound.id);

        const featureSource = {
            data: layerEntity,
            isVisible: source?.isVisible || false,
            maxzoom: layerStyle.maxzoom,
            minzoom: layerStyle.minzoom,
            name: layerEntity['bp_etiquet'],
            sourceId: layerFound.id,
            style: layerStyle.style,
            type: MapSourceTypeEnum.LAYER, //in synoptic there could only be layer source
        } satisfies MapBaseSource<unknown>;

        return new EntityInformation(featureLocation, featureSource, type, layerStyle);
    }

    public flyToEntityLocation(
        featureSource: MapBaseSource<unknown>,
        nodeUuid: string,
        featureLocation: EntityLocation,
        entityTypeEnum: LayerStyleTypeEnum,
    ): void {
        this.moveToLocation(featureSource, featureLocation, entityTypeEnum);

        this.map?.once('idle', async () => {
            if (!featureSource.isVisible) {
                this.snackBarService.openWarningSnackBar('unableToHighlightLayerNotVisible');

                return;
            }

            const filteredFeature = await this.waitForRenderedFeatureByUuid(nodeUuid, featureLocation, entityTypeEnum);

            if (!filteredFeature) {
                this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

                return;
            }

            const formatedFeature = this.modifyFeatureSource(featureSource, filteredFeature);

            this.mapHighlightEntityService.resetHighlight(
                this.mapHighlightEntityService.oldSource(),
                this.mapHighlightEntityService.oldSourceType(),
            );

            this.mapHighlightEntityService.manageHighlightFeature(formatedFeature, [nodeUuid], entityTypeEnum);
        });
    }

    public async moveMapToEntityLocationFromSynopticNode(
        nodeUuid: string,
        depositId: string,
        entityTypeEnum: LayerStyleTypeEnum,
        layerFromSyno?: GraceThdSynopticItem | GenericSynopticItem,
    ): Promise<void> {
        const layerListBloc = this.layerListBloc?.value()?.items;

        if (!layerListBloc || !layerFromSyno) {
            return;
        }

        let entityInformation = await this.findEntityInActiveLayers(layerListBloc, nodeUuid);

        if (!entityInformation.featureLocation) {
            entityInformation = await this.findEntityInLayerService(layerFromSyno, nodeUuid, depositId, entityTypeEnum);
        }

        if (!entityInformation.featureLocation || !entityInformation.featureSource || !entityInformation.type) {
            this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

            return;
        }

        this.flyToEntityLocation(
            entityInformation.featureSource,
            nodeUuid,
            entityInformation.featureLocation,
            entityTypeEnum,
        );
    }

    public async redirectMapEntityToSynoptic(
        depositId: string,
        znUuidAttribute: string,
        dataEntity: EntityProperties,
    ): Promise<void> {
        const path = [RoutePathEnum.PROJECTS, this.projectBloc.value()?.id, RoutePathEnum.SYNOPTIC];
        await this.router.navigate(path);

        if (!this.selectSynopticService.depositList().length) {
            await this.selectSynopticService.loadSynopticData();
        }

        const selectedDeposit: DepositWithStandard | undefined = this.selectSynopticService
            .depositList()
            .find((deposit: DepositWithStandard) => deposit.id === depositId);

        if (!selectedDeposit) {
            this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

            return;
        }

        this.selectSynopticService.selectedDeposit.set(selectedDeposit);

        await this.selectSynopticService.selectSynopticZone(selectedDeposit);

        const synopticZone = this.selectSynopticService
            .synopticZones()
            .find((synopticZone: SynopticZone) => synopticZone.zn_uuid === znUuidAttribute);

        if (!synopticZone) {
            return;
        }

        await this.selectSynopticService.selectSynoptic(synopticZone);
        void this.synopticGraphService.focusNode(dataEntity);
    }

    protected override addSourceStyle(sourceId: string, style: MapboxStyle[]): MapboxStyle[] | undefined {
        return style.map((style: MapboxStyle): MapboxStyle => {
            return {
                ...style,
                id: this.mapLayerService.createLayerId(sourceId, style.id),
                source: sourceId,
            };
        });
    }

    private convertMapboxTypeToLayerStyleTypeEnum(featureLocation: EntityLocation): string | undefined {
        const type: string | undefined = featureLocation?.type?.toLowerCase();

        if (!type) {
            return;
        }

        return type === 'linestring' ? LayerStyleTypeEnum.LINE : type;
    }

    private async findEntityInActiveLayers(layers: Layer[], nodeUuid: string): Promise<EntityInformation> {
        let featureSource: MapBaseSource<unknown> | undefined;
        let featureLocation: EntityLocation | undefined;
        let type: string | undefined;
        let layerStyle: MapboxStyleWithZoom | undefined;

        for (const layer of layers) {
            const features = this.map?.querySourceFeatures(layer.id);
            const foundFeature = features?.find((feature): boolean => {
                AssertService.isString(feature.properties['uuid']);

                return feature.properties['uuid'] === nodeUuid;
            });

            if (!foundFeature) {
                continue;
            }

            layerStyle = await firstValueFrom(
                this.layerStylesService.layerStyleControllerGetOne({
                    id: layer?.layerStyle ?? DEFAULT_LAYER_STYLE_ID,
                }),
            );

            featureLocation = foundFeature.geometry as EntityLocation;

            type = this.convertMapboxTypeToLayerStyleTypeEnum(featureLocation);

            const sourceStyle: MapboxStyle[] | undefined = this.addSourceStyle(layer.id, layerStyle.style);

            featureSource = {
                data: foundFeature,
                isVisible: true,
                name: foundFeature.properties['bp_etiquet'],
                sourceId: layer.id,
                style: sourceStyle,
                type: type,
            } as MapBaseSource<unknown>;

            break;
        }

        return new EntityInformation(featureLocation, featureSource, type, layerStyle);
    }

    private findFeatureByUuid(
        nodeUuid: string,
        featureLocation: EntityLocation,
        entityTypeEnum: LayerStyleTypeEnum,
    ): MapGeoJSONFeature | undefined {
        if (!featureLocation.coordinates) {
            throw new Error('Feature location coordinates are missing');
        }

        const bbox = this.formatCoordinates(featureLocation.coordinates, entityTypeEnum);

        if (!bbox) {
            return;
        }

        return this.findFeatureInBbox(bbox, nodeUuid);
    }

    private findFeatureInBbox(bbox: [PointLike, PointLike], nodeUuid: string): MapGeoJSONFeature | undefined {
        return this.map
            ?.queryRenderedFeatures(bbox)
            .find((feature: MapLibreMapGeoJSONFeature) => feature.properties['uuid'] === nodeUuid);
    }

    private formatCoordinates(
        coordinates: Position[] | Position,
        type: LayerStyleTypeEnum,
    ): [PointLike, PointLike] | undefined {
        switch (type) {
            case LayerStyleTypeEnum.LINE: {
                if (!this.isAvailableLineStringCoordinate(coordinates)) {
                    this.snackBarService.openErrorSnackBar('invalidCoordinates');

                    return;
                }

                const lineCoords = coordinates as Position[];
                const projectedPoints = lineCoords.map(([lng, lat]) => this.map!.project(new LngLat(lng, lat)));

                const xs: number[] = projectedPoints.map((pt) => pt.x);
                const ys: number[] = projectedPoints.map((pt) => pt.y);

                return [
                    [Math.min(...xs), Math.min(...ys)],
                    [Math.max(...xs), Math.max(...ys)],
                ];
            }

            case LayerStyleTypeEnum.POINT:
            case LayerStyleTypeEnum.SYMBOL: {
                const [lng, lat] = coordinates as Position;
                const point = this.map?.project(new LngLat(lng, lat));
                if (!point) {
                    return;
                }

                return [
                    [point.x, point.y],
                    [point.x, point.y],
                ];
            }

            default:
                return;
        }
    }

    private getSpecificIdType(id: string, mapSourceId: string): string {
        return id.replace(`${mapSourceId}-`, '');
    }

    private getZoomLevel(featureSource: MapBaseSource<unknown>): number {
        if (
            featureSource.minzoom &&
            featureSource.maxzoom &&
            !this.isMapZoomInside(featureSource.minzoom, featureSource.maxzoom)
        ) {
            return featureSource.minzoom;
        }

        return this.map!.getZoom();
    }

    private isAvailableLineStringCoordinate(input: unknown): input is Position[] {
        return (
            Array.isArray(input) &&
            input.length > 1 &&
            input.every(
                (coord): coord is Position =>
                    Array.isArray(coord) &&
                    coord.length === 2 &&
                    typeof coord[0] === 'number' &&
                    typeof coord[1] === 'number',
            )
        );
    }

    private isMapZoomInside(min: number, max: number): boolean {
        return min <= this.map!.getZoom() && this.map!.getZoom() <= max;
    }

    private isMatchingLayer(
        layer: Layer,
        syno: GraceThdSynopticItem | GenericSynopticItem,
        entityId: string,
        type: LayerStyleTypeEnum,
    ): boolean {
        switch (type) {
            case LayerStyleTypeEnum.POINT:
            case LayerStyleTypeEnum.SYMBOL: {
                if (syno.from_uuid === entityId) {
                    return layer.tableName === syno.from_table;
                }

                return layer.tableName === syno.to_table;
            }
            case LayerStyleTypeEnum.LINE:
                return layer.tableName === syno.edge_table;
            default:
                return false;
        }
    }

    private listenBroadcastHighlightSynopticNodeChannel(): void {
        this.broadcastHighlightSynopticNodeChannel.onmessage = async (
            event: MessageEvent<BroadcastChannelEvent<SynopticMapData>>,
        ): Promise<void> => {
            if (event.data.source !== BroadcastChannelSourceEnum.SYNOPTIC_PLAN || this.homeService.isExternalPage()) {
                return;
            }

            if (event.data.message === BroadcastChannelMessageEnum.RESET_HIGHLIGHT_SYNOPTIC) {
                this.homeService.synopticExtPageOpen.set(false);

                this.mapHighlightEntityService.resetHighlight();

                return;
            }

            if (!event.data.additionalData) {
                return;
            }

            this.homeService.synopticExtPageOpen.set(true);

            await this.moveMapToEntityLocationFromSynopticNode(
                event.data.additionalData.node,
                event.data.additionalData.depositId,
                event.data.additionalData.layerStyleType,
                event.data.additionalData.synopticItem,
            );
        };
    }

    private modifyFeatureSource(
        featureSource: MapBaseSource<unknown>,
        filteredFeature: MapGeoJSONFeature,
    ): MapBaseSource<unknown> {
        return {
            ...featureSource,
            style: featureSource.style.map((style) => {
                return {
                    ...style,
                    id: this.getSpecificIdType(style.id, featureSource.sourceId),
                };
            }),
            type: this.getSpecificIdType(filteredFeature.layer.id, featureSource.sourceId) as MapSourceTypeEnum,
        };
    }

    private moveToLocation(
        featureSource: MapBaseSource<unknown>,
        featureLocation: EntityLocation,
        entityTypeEnum: LayerStyleTypeEnum,
    ): void {
        if (entityTypeEnum === LayerStyleTypeEnum.SYMBOL) {
            this.map?.flyTo({
                center: featureLocation.coordinates as LngLatLike,
                zoom: this.getZoomLevel(featureSource),
            });

            return;
        }

        const coords = featureLocation.coordinates;

        if (!coords || !this.isAvailableLineStringCoordinate(coords)) {
            this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

            return;
        }

        const positionCoords: Position[] = [...coords];

        const line = lineString(positionCoords);
        const [minX, minY, maxX, maxY] = bbox(line);

        this.map?.fitBounds(
            [
                [minX, minY],
                [maxX, maxY],
            ],
            {
                duration: 500,
                maxZoom: this.getZoomLevel(featureSource),
                padding: 50,
            },
        );
    }

    private waitForRenderedFeatureByUuid(
        nodeUuid: string,
        featureLocation: EntityLocation,
        entityTypeEnum: LayerStyleTypeEnum,
        timeoutInMilliseconds = 5000,
    ): Promise<MapGeoJSONFeature | undefined> {
        const startedAt = Date.now();

        return new Promise((resolve) => {
            const checkFeature = (): void => {
                const feature = this.findFeatureByUuid(nodeUuid, featureLocation, entityTypeEnum);

                if (feature) {
                    this.map?.off('render', checkFeature);
                    resolve(feature);

                    return;
                }

                if (Date.now() - startedAt >= timeoutInMilliseconds) {
                    this.map?.off('render', checkFeature);
                    resolve(undefined);

                    return;
                }
            };

            this.map?.on('render', checkFeature);
            checkFeature();
        });
    }
}
